import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { gsap } from 'gsap';
import { registerRsPlugins, RsMotionModule, RsMotionService } from '../motion';

/**
 * Closing call-to-action for the service pages.
 *
 * Standalone and self-contained so a service page only needs one tag to gain it.
 * Owns its own scroll reveal and ambient particle motion, both reverted on
 * destroy. SSR-safe and reduced-motion aware via RsMotionService.
 */
@Component({
  selector: 'app-service-cta',
  standalone: true,
  imports: [CommonModule, RouterModule, RsMotionModule],
  templateUrl: './service-cta.component.html',
  styleUrls: ['./service-cta.component.css'],
})
export class ServiceCtaComponent implements AfterViewInit, OnDestroy {
  @ViewChild('ctaRoot') ctaRoot?: ElementRef<HTMLElement>;

  private ctx?: gsap.Context;
  private cleanups: Array<() => void> = [];

  constructor(private motion: RsMotionService, private zone: NgZone) {}

  ngAfterViewInit(): void {
    const root = this.ctaRoot?.nativeElement;
    if (!root || !this.motion.enabled) return;

    this.motion.arm();
    registerRsPlugins();

    this.zone.runOutsideAngular(() => {
      this.ctx = gsap.context(() => {
        const tl = gsap.timeline({
          defaults: { ease: 'power3.out' },
          scrollTrigger: { trigger: root, start: 'top 88%', once: true },
        });

        tl.from('.sc__panel', { opacity: 0, y: 38, scale: 0.97, duration: 0.85 })
          .from('.sc__ico', { opacity: 0, scale: 0.6, duration: 0.6, ease: 'back.out(2)' }, '-=0.5')
          .from('.sc__copy > *', { opacity: 0, y: 20, duration: 0.55, stagger: 0.08 }, '-=0.45')
          .from('.sc__actions > *', { opacity: 0, y: 18, duration: 0.5, stagger: 0.12 }, '-=0.35');

        // ambient particle drift on the transform channel
        gsap.utils.toArray<HTMLElement>('.sc__particle').forEach((dot, i) => {
          gsap.to(dot, {
            y: -14,
            x: i % 2 === 0 ? 8 : -8,
            duration: 4 + (i % 3),
            delay: i * 0.25,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
          });
        });
      }, root);

      this.cleanups.push(this.motion.pauseOffscreen(root));
    });
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.ctx?.revert();
    this.ctx = undefined;
  }
}
